import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  TextField,
} from "@mui/material";
import axios from "axios";
import { Goex_URL, GOOGLE_MAPS_API_KEY } from "../../config/api";

export default function AddressDialog({
  open,
  onClose,
  mode = "add",
  initialData = null,
  onSave,
  saving = false,
}) {
  /* ---------------- STEP ---------------- */
  const [step, setStep] = useState("choose");

  /* ---------------- FLAGS ---------------- */
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeError, setPincodeError] = useState("");
  const [openLocationConfirm, setOpenLocationConfirm] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false); // 🔥 important

  /* ---------------- USER FROM SESSION ---------------- */
  const authUser = JSON.parse(sessionStorage.getItem("authUserData") || "{}");

  /* ---------------- FORM ---------------- */
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    lat: null,
    lng: null,
  });

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    if (!open) return;

    setLocationError("");
    setPincodeError("");
    setAutoFilled(false);

    if (mode === "add") {
      setStep("choose");
      setForm({
        name: authUser?.name || "",
        mobile: authUser?.phone || authUser?.mobile || "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        pincode: "",
        lat: null,
        lng: null,
      });
    }

    if (mode === "edit" && initialData) {
      setStep("form");
      setAutoFilled(false);
      setForm({
        name: initialData.name || "",
        mobile: initialData.mobile || "",
        address1: initialData.address1 || "",
        address2: initialData.address2 || "",
        city: initialData.city || "",
        state: initialData.state || "",
        pincode: initialData.pincode || "",
        lat: initialData.lat ?? null,
        lng: initialData.lng ?? null,
      });
    }
  }, [open, mode, initialData]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  /* ---------------- SAVE ---------------- */
  const handleSave = () => {
    onSave?.({
      ...form,
      lat: form.lat,
      lng: form.lng,
    });
  };

  /* ---------------- GEO LOCATION ---------------- */
  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported");
      return;
    }

    setLocating(true);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        setForm((p) => ({ ...p, lat, lng }));
        await reverseGeocode(lat, lng);

        setAutoFilled(true);
        setLocating(false);
        setStep("form");
      },
      () => {
        setLocating(false);
        setLocationError("Location permission denied");
        setStep("form");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  /* ---------------- REVERSE GEOCODE ---------------- */
  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: {
            latlng: `${lat},${lng}`,
            key: GOOGLE_MAPS_API_KEY,
          },
        }
      );

      const components = res.data.results?.[0]?.address_components || [];
      const get = (t) =>
        components.find((c) => c.types.includes(t))?.long_name || "";

      const pincode = get("postal_code");
      const city =
        get("locality") || get("administrative_area_level_2");
      const state = get("administrative_area_level_1");

      setForm((p) => ({ ...p, pincode, city, state }));

      if (pincode?.length === 6) fetchPincodeDetails(pincode);
    } catch {
      setLocationError("Failed to detect address");
    }
  };

  /* ---------------- PINCODE API ---------------- */
  const fetchPincodeDetails = async (pincode) => {
    if (pincode.length !== 6) return;

    try {
      setPincodeLoading(true);
      setPincodeError("");

      const res = await axios.get(`${Goex_URL}pincode/${pincode}`);

      if (res.data?.success) {
        setForm((p) => ({
          ...p,
          state: res.data.state || p.state,
          city: res.data.district || p.city,
        }));
        setAutoFilled(true);
      } else {
        setPincodeError("Invalid pincode");
      }
    } catch {
      setPincodeError("Failed to fetch location");
    } finally {
      setPincodeLoading(false);
    }
  };

  /* ---------------- RENDER ---------------- */
  return (
    <Dialog open={open} onClose={onClose} fullWidth sx={{
    "& .MuiDialog-paper": {
      margin: "8px",                 // 👈 reduce outer margin
      width: "calc(100% - 16px)",    // 👈 prevents overflow
    },
  }}>
      {/* ---------- STEP 1 ---------- */}
      {mode === "add" && step === "choose" && (
        <Box p={3}>
          <Typography variant="h6" fontWeight={900}>
            Add Address
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            How would you like to add your address?
          </Typography>

          <Stack spacing={2} mt={3}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setOpenLocationConfirm(true)}
            >
              📍 Use Current Location
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={() => setStep("form")}
            >
              ✍️ Enter Manually
            </Button>
          </Stack>
        </Box>
      )}

      {/* ---------- FORM ---------- */}
      {step === "form" && (
        <Box p={3}>
          <Typography variant="h6" fontWeight={900}>
            {mode === "edit" ? "Edit Address" : "Add Address"}
          </Typography>

          <Stack spacing={1.5} mt={2}>
            <TextField label="Name" value={form.name} onChange={(e) => update("name", e.target.value)} />
            <TextField label="Mobile" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} />
            <TextField label="Address Line 1" value={form.address1} onChange={(e) => update("address1", e.target.value)} />
            <TextField label="Address Line 2" value={form.address2} onChange={(e) => update("address2", e.target.value)} />

            <TextField
              label="Pincode"
              value={form.pincode}
              InputProps={{ readOnly: autoFilled }}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                update("pincode", v);
                if (v.length === 6) fetchPincodeDetails(v);
              }}
            />

            {pincodeLoading && <Typography variant="caption">Fetching location…</Typography>}
            {pincodeError && <Typography variant="caption" color="error">{pincodeError}</Typography>}

            <TextField label="City" value={form.city} InputProps={{ readOnly: autoFilled }} />
            <TextField label="State" value={form.state} InputProps={{ readOnly: autoFilled }} />

            {locationError && <Typography variant="caption" color="error">{locationError}</Typography>}

            <Divider />

            <Stack direction="row" spacing={1.5}>
              <Button fullWidth variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button fullWidth variant="contained" disabled={saving} onClick={handleSave}>
                {mode === "edit" ? "Update" : "Save"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* ---------- CONFIRM LOCATION ---------- */}
      <Dialog open={openLocationConfirm} onClose={() => setOpenLocationConfirm(false)}>
        <Box p={3} maxWidth={360}>
          <Typography fontWeight={900}>Allow location access?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            We’ll auto-fill your address using your current location.
          </Typography>

          <Stack direction="row" spacing={1.5} mt={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setOpenLocationConfirm(false);
                setStep("form");
              }}
            >
              Not now
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setOpenLocationConfirm(false);
                detectCurrentLocation();
              }}
            >
              Allow
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Dialog>
  );
}




// // src/dialogs/AddressDialog.jsx
// import React, { useEffect, useState } from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   TextField,
//   CircularProgress,
// } from "@mui/material";
// import axios from "axios";

// /**
//  * AddressDialog
//  *
//  * Props:
//  * - open
//  * - onClose
//  * - mode ("add" | "edit")
//  * - initialData (object | null)
//  * - onSave (payload) => Promise | void
//  * - saving (boolean)
//  */
// export default function AddressDialog({
//   open,
//   onClose,
//   mode = "add",
//   initialData = null,
//   onSave,
//   saving = false,
// }) {

//   const [pincodeLoading, setPincodeLoading] = useState(false);
//   const [pincodeError, setPincodeError] = useState("");
//   const [locating, setLocating] = useState(false);
//   const [locationError, setLocationError] = useState("");
//   const [openLocationConfirm, setOpenLocationConfirm] = useState(false);

//   const [form, setForm] = useState({
//     name: "",
//     mobile: "",
//     address1: "",
//     address2: "",
//     city: "",
//     state: "",
//     pincode: "",
//     lat: "",
//     lng: "",
//   });

//   useEffect(() => {
//     if (mode === "edit" && initialData) {
//       setForm({
//         name: initialData.name || "",
//         mobile: initialData.mobile || "",
//         address1: initialData.address1 || "",
//         address2: initialData.address2 || "",
//         city: initialData.city || "",
//         state: initialData.state || "",
//         pincode: initialData.pincode || "",
//         lat: initialData.lat ?? "",
//         lng: initialData.lng ?? "",
//       });
//     }

//     if (mode === "add") {
//       setForm({
//         name: "",
//         mobile: "",
//         address1: "",
//         address2: "",
//         city: "",
//         state: "",
//         pincode: "",
//         lat: "",
//         lng: "",
//       });
//     }
//   }, [mode, initialData, open]);

//   const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

//   const validate = () => {
//     if (!form.name.trim()) return "Name is required";
//     if (!form.mobile.trim()) return "Mobile is required";
//     if (!form.address1.trim()) return "Address Line 1 is required";
//     if (!form.city.trim()) return "City is required";
//     if (!form.state.trim()) return "State is required";
//     if (!form.pincode.trim()) return "Pincode is required";
//     return null;
//   };

//   const handleSave = () => {
//     const err = validate();
//     if (err) return alert(err);

//     const payload = {
//       ...form,
//       lat: form.lat === "" ? null : Number(form.lat),
//       lng: form.lng === "" ? null : Number(form.lng),
//     };

//     onSave?.(payload);
//   };

//   const detectCurrentLocation = () => {
//   if (!navigator.geolocation) {
//     setLocationError("Geolocation not supported");
//     return;
//   }

//   setLocating(true);
//   setLocationError("");

//   navigator.geolocation.getCurrentPosition(
//     async (position) => {
//       const lat = position.coords.latitude;
//       const lng = position.coords.longitude;

//       update("lat", lat);
//       update("lng", lng);

//       // 🔹 Reverse geocode
//       await reverseGeocode(lat, lng);

//       setLocating(false);
//     },
//     (error) => {
//       setLocating(false);
//       setLocationError("Location access denied");
//     },
//     { enableHighAccuracy: true, timeout: 15000 }
//   );
// };


// const reverseGeocode = async (lat, lng) => {
//   try {
//     const res = await axios.get(
//       "https://maps.googleapis.com/maps/api/geocode/json",
//       {
//         params: {
//           latlng: `${lat},${lng}`,
//           key: "YOUR_GOOGLE_MAPS_API_KEY",
//         },
//       }
//     );

//     const components = res.data.results[0]?.address_components || [];

//     const get = (type) =>
//       components.find((c) => c.types.includes(type))?.long_name || "";

//     const pincode = get("postal_code");
//     const city =
//       get("locality") ||
//       get("administrative_area_level_2");
//     const state = get("administrative_area_level_1");

//     setForm((prev) => ({
//       ...prev,
//       pincode,
//       city,
//       state,
//     }));

//     // 🔹 If pincode found → fetch district/state via your API
//     if (pincode?.length === 6) {
//       fetchPincodeDetails(pincode);
//     }
//   } catch (err) {
//     setLocationError("Failed to fetch address from location");
//   }
// };



//   const fetchPincodeDetails = async (pincode) => {
//   if (!pincode || pincode.length !== 6) return;

//   try {
//     setPincodeLoading(true);
//     setPincodeError("");

//     const res = await axios.get(
//       `https://goelectronix.in/api/v1/pincode/${pincode}`
//     );

//     if (res.data?.success) {
//       setForm((prev) => ({
//         ...prev,
//         state: res.data.state || prev.state,
//         city: res.data.district || prev.city,
//       }));
//     } else {
//       setPincodeError("Invalid pincode");
//     }
//   } catch (err) {
//     setPincodeError("Failed to fetch location");
//   } finally {
//     setPincodeLoading(false);
//   }
// };

//   return (
//     <Dialog open={open} onClose={onClose} fullWidth>
//       <Box p={3}>
//         <Typography variant="h6" fontWeight={900}>
//           {mode === "edit" ? "Edit Address" : "Add Address"}
//         </Typography>

//         <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
//           Please enter complete address details
//         </Typography>

//         <Stack spacing={1.5} mt={2}>
//           <TextField
//             label="Name"
//             value={form.name}
//             onChange={(e) => update("name", e.target.value)}
//             fullWidth
//           />

//           <TextField
//             label="Mobile"
//             value={form.mobile}
//             onChange={(e) => update("mobile", e.target.value)}
//             fullWidth
//           />

//           {mode === "add" && (
//             <Button
//               variant="outlined"
//               onClick={() => setOpenLocationConfirm(true)}
//               disabled={locating}
//             >
//               Use Current Location
//             </Button>

//           )}

//           {locating && (
//             <Stack direction="row" spacing={1} alignItems="center">
//               <CircularProgress size={16} />
//               <Typography variant="caption">
//                 Fetching your location…
//               </Typography>
//             </Stack>
//           )}

//           {locationError && (
//             <Typography variant="caption" color="error">
//               {locationError}
//             </Typography>
//           )}



//           <TextField
//             label="Address Line 1"
//             value={form.address1}
//             onChange={(e) => update("address1", e.target.value)}
//             fullWidth
//           />

//           <TextField
//             label="Address Line 2"
//             value={form.address2}
//             onChange={(e) => update("address2", e.target.value)}
//             fullWidth
//           />

//           <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
//             <TextField
//               label="Pincode"
//               value={form.pincode}
//               onChange={(e) => {
//                 const value = e.target.value.replace(/\D/g, "").slice(0, 6);
//                 update("pincode", value);

//                 if (value.length === 6) {
//                   fetchPincodeDetails(value);
//                 } else {
//                   setPincodeError("");
//                 }
//               }}
//               fullWidth
//             />

//             {pincodeLoading && (
//               <Stack direction="row" spacing={1} alignItems="center">
//                 <CircularProgress size={16} />
//                 <Typography variant="caption" color="text.secondary">
//                   Fetching location…
//                 </Typography>
//               </Stack>
//             )}

//             {pincodeError && (
//               <Typography variant="caption" color="error">
//                 {pincodeError}
//               </Typography>
//             )}
            
//             <TextField
//               label="City"
//               value={form.city}
//               onChange={(e) => update("city", e.target.value)}
//               fullWidth
//             />

//             <TextField
//               label="State"
//               value={form.state}
//               onChange={(e) => update("state", e.target.value)}
//               fullWidth
//             />

//           </Stack>

//           <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
//             <TextField
//               label="Latitude (optional)"
//               value={form.lat}
//               onChange={(e) => update("lat", e.target.value)}
//               fullWidth
//             />
//             <TextField
//               label="Longitude (optional)"
//               value={form.lng}
//               onChange={(e) => update("lng", e.target.value)}
//               fullWidth
//             />
//           </Stack>

//           {saving && (
//             <Stack direction="row" spacing={1} alignItems="center">
//               <CircularProgress size={18} />
//               <Typography variant="caption" color="text.secondary">
//                 Saving address...
//               </Typography>
//             </Stack>
//           )}

//           <Divider />

//           <Stack direction="row" spacing={1.5}>
//             <Button
//               fullWidth
//               variant="outlined"
//               disabled={saving}
//               onClick={onClose}
//             >
//               Cancel
//             </Button>

//             <Button
//               fullWidth
//               variant="contained"
//               disabled={saving}
//               onClick={handleSave}
//             >
//               {mode === "edit" ? "Update" : "Save"}
//             </Button>
//           </Stack>
//         </Stack>
//       </Box>

//       <Dialog
//           open={openLocationConfirm}
//           onClose={() => setOpenLocationConfirm(false)}
//         >
//           <Box p={3} maxWidth={360}>
//             <Typography fontWeight={900}>
//               Allow location access?
//             </Typography>

//             <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
//               We’ll use your current location to automatically fill your address.
//               You can also enter details manually.
//             </Typography>

//             <Stack direction="row" spacing={1.5} mt={3}>
//               <Button
//                 fullWidth
//                 variant="outlined"
//                 onClick={() => setOpenLocationConfirm(false)}
//               >
//                 Not now
//               </Button>

//               <Button
//                 fullWidth
//                 variant="contained"
//                 onClick={() => {
//                   setOpenLocationConfirm(false);
//                   detectCurrentLocation(); // 🔥 start location fetch
//                 }}
//               >
//                 Allow
//               </Button>
//             </Stack>
//           </Box>
//         </Dialog>

//     </Dialog>
//   );
// }




// // src/dialogs/AddressDialog.jsx
// import React, { useEffect, useState } from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   TextField,
//   CircularProgress,
// } from "@mui/material";
// import axios from "axios";

// /**
//  * AddressDialog
//  *
//  * Props:
//  * - open
//  * - onClose
//  * - mode ("add" | "edit")
//  * - initialData (object | null)
//  * - onSave (payload) => Promise | void
//  * - saving (boolean)
//  */
// export default function AddressDialog({
//   open,
//   onClose,
//   mode = "add",
//   initialData = null,
//   onSave,
//   saving = false,
// }) {

//   const [pincodeLoading, setPincodeLoading] = useState(false);
//   const [pincodeError, setPincodeError] = useState("");

//   const [form, setForm] = useState({
//     name: "",
//     mobile: "",
//     address1: "",
//     address2: "",
//     city: "",
//     state: "",
//     pincode: "",
//     lat: "",
//     lng: "",
//   });

//   useEffect(() => {
//     if (mode === "edit" && initialData) {
//       setForm({
//         name: initialData.name || "",
//         mobile: initialData.mobile || "",
//         address1: initialData.address1 || "",
//         address2: initialData.address2 || "",
//         city: initialData.city || "",
//         state: initialData.state || "",
//         pincode: initialData.pincode || "",
//         lat: initialData.lat ?? "",
//         lng: initialData.lng ?? "",
//       });
//     }

//     if (mode === "add") {
//       setForm({
//         name: "",
//         mobile: "",
//         address1: "",
//         address2: "",
//         city: "",
//         state: "",
//         pincode: "",
//         lat: "",
//         lng: "",
//       });
//     }
//   }, [mode, initialData, open]);

//   const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

//   const validate = () => {
//     if (!form.name.trim()) return "Name is required";
//     if (!form.mobile.trim()) return "Mobile is required";
//     if (!form.address1.trim()) return "Address Line 1 is required";
//     if (!form.city.trim()) return "City is required";
//     if (!form.state.trim()) return "State is required";
//     if (!form.pincode.trim()) return "Pincode is required";
//     return null;
//   };

//   const handleSave = () => {
//     const err = validate();
//     if (err) return alert(err);

//     const payload = {
//       ...form,
//       lat: form.lat === "" ? null : Number(form.lat),
//       lng: form.lng === "" ? null : Number(form.lng),
//     };

//     onSave?.(payload);
//   };


//   const fetchPincodeDetails = async (pincode) => {
//   if (!pincode || pincode.length !== 6) return;

//   try {
//     setPincodeLoading(true);
//     setPincodeError("");

//     const res = await axios.get(
//       `https://goelectronix.in/api/v1/pincode/${pincode}`
//     );

//     if (res.data?.success) {
//       setForm((prev) => ({
//         ...prev,
//         state: res.data.state || prev.state,
//         city: res.data.district || prev.city,
//       }));
//     } else {
//       setPincodeError("Invalid pincode");
//     }
//   } catch (err) {
//     setPincodeError("Failed to fetch location");
//   } finally {
//     setPincodeLoading(false);
//   }
// };

//   return (
//     <Dialog open={open} onClose={onClose} fullWidth>
//       <Box p={3}>
//         <Typography variant="h6" fontWeight={900}>
//           {mode === "edit" ? "Edit Address" : "Add Address"}
//         </Typography>

//         <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
//           Please enter complete address details
//         </Typography>

//         <Stack spacing={1.5} mt={2}>
//           <TextField
//             label="Name"
//             value={form.name}
//             onChange={(e) => update("name", e.target.value)}
//             fullWidth
//           />

//           <TextField
//             label="Mobile"
//             value={form.mobile}
//             onChange={(e) => update("mobile", e.target.value)}
//             fullWidth
//           />

//           <TextField
//             label="Address Line 1"
//             value={form.address1}
//             onChange={(e) => update("address1", e.target.value)}
//             fullWidth
//           />

//           <TextField
//             label="Address Line 2"
//             value={form.address2}
//             onChange={(e) => update("address2", e.target.value)}
//             fullWidth
//           />

//           <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
//             <TextField
//               label="Pincode"
//               value={form.pincode}
//               onChange={(e) => {
//                 const value = e.target.value.replace(/\D/g, "").slice(0, 6);
//                 update("pincode", value);

//                 if (value.length === 6) {
//                   fetchPincodeDetails(value);
//                 } else {
//                   setPincodeError("");
//                 }
//               }}
//               fullWidth
//             />

//             {pincodeLoading && (
//               <Stack direction="row" spacing={1} alignItems="center">
//                 <CircularProgress size={16} />
//                 <Typography variant="caption" color="text.secondary">
//                   Fetching location…
//                 </Typography>
//               </Stack>
//             )}

//             {pincodeError && (
//               <Typography variant="caption" color="error">
//                 {pincodeError}
//               </Typography>
//             )}

//             <TextField
//               label="City"
//               value={form.city}
//               onChange={(e) => update("city", e.target.value)}
//               fullWidth
//             />

//             <TextField
//               label="State"
//               value={form.state}
//               onChange={(e) => update("state", e.target.value)}
//               fullWidth
//             />

//           </Stack>

//           <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
//             <TextField
//               label="Latitude (optional)"
//               value={form.lat}
//               onChange={(e) => update("lat", e.target.value)}
//               fullWidth
//             />
//             <TextField
//               label="Longitude (optional)"
//               value={form.lng}
//               onChange={(e) => update("lng", e.target.value)}
//               fullWidth
//             />
//           </Stack>

//           {saving && (
//             <Stack direction="row" spacing={1} alignItems="center">
//               <CircularProgress size={18} />
//               <Typography variant="caption" color="text.secondary">
//                 Saving address...
//               </Typography>
//             </Stack>
//           )}

//           <Divider />

//           <Stack direction="row" spacing={1.5}>
//             <Button
//               fullWidth
//               variant="outlined"
//               disabled={saving}
//               onClick={onClose}
//             >
//               Cancel
//             </Button>

//             <Button
//               fullWidth
//               variant="contained"
//               disabled={saving}
//               onClick={handleSave}
//             >
//               {mode === "edit" ? "Update" : "Save"}
//             </Button>
//           </Stack>
//         </Stack>
//       </Box>
//     </Dialog>
//   );
// }
